import { useStytchOrganization } from "@stytch/react/b2b";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "touchtab-portal-app-state";

export type AppState = {
    title: string | null;
    // The org the session AUTHENTICATED into — a stable identity. Drives the URL slug and in-app links, and is
    // NOT changed by the org picker. The org being VIEWED is organizationId (below), which a super-admin can
    // point at any org without touching the slug.
    organizationSlug: string | null;
    // The org currently being VIEWED / acted on. For a super-admin this is the picker's selection (persisted,
    // so it restores to the last-viewed org); for everyone else it is pinned to their authenticated org.
    organizationId: string | null;
    brandId: string | null;
    locationId: string | null;
};

const initialState: AppState = {
    title: null,
    organizationSlug: null,
    organizationId: null,
    brandId: null,
    locationId: null,
};

type AppContextType = AppState & {
    setTitle: (title: string) => void;
    setOrganizationSlug: (slug: string | null) => void;
    setOrganizationId: (id: string | null) => void;
    // Switch the VIEWED org (the super-admin org picker) — resets brand/location beneath it.
    setViewedOrganizationId: (id: string | null) => void;
    setBrandId: (id: string | null) => void;
    setLocationId: (id: string | null) => void;
    reset: () => void;
    isSuperAdmin: () => boolean;
    // True when a super-admin is VIEWING their own (TouchTab) org — used to restrict the menu to the
    // TouchTab-only surfaces (Admin → Configuration/Oracle Cert, Users).
    isViewingSuperAdminOrg: () => boolean;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = useState<AppState>(() => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}");
        } catch {
            return {};
        }
    });

    // Functional, so two updates in the same tick compose instead of overwriting each other.
    //
    // This used to build the next state from `state` in the enclosing closure. Two callers updating in one
    // React batch each started from the SAME snapshot, so the second silently undid the first. It showed up
    // as the org/brand picker not moving after choosing "Leave": the scope change and the brand editor's
    // unmount (which resets the app-bar title) landed together, and the title write — holding a snapshot
    // taken before the scope changed — put the old scope back. Locations looked fine only because that
    // editor has no unmount cleanup to collide with (TTD-149).
    const update = useCallback((patch: Partial<AppState>) => {
        setState((previous) => ({ ...previous, ...patch }));
    }, []);

    // Persisted from an effect rather than inside update(), because a state updater must stay pure — React
    // may call it more than once for the same change, and writing storage from there would be a side effect
    // riding on how often that happens.
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, [state]);

    // Identity = the org THIS SESSION authenticated into, straight from Stytch. This is the source of truth
    // for super-admin status and mirrors state.organizationSlug (which is now also login identity, not the
    // viewed org). Stytch's value is populated on session restore, so it survives a refresh that never
    // renders the Login screen.
    const { organization } = useStytchOrganization();
    const isSuperAdmin = () => organization?.organization_slug === "touchtab";
    // The super-admin is viewing their OWN org when the selected (viewed) org is their authenticated org.
    const isViewingSuperAdminOrg = () =>
        isSuperAdmin() && !!state.organizationId && state.organizationId === organization?.organization_id;

    return (
        <AppContext.Provider
            value={{
                ...state,
                setTitle: (title) => update({ title }),
                // organizationSlug is identity (the authenticated org) — set once at login, never by the
                // picker, so it does NOT touch the viewed org or the scope below it.
                setOrganizationSlug: (slug) => update({ organizationSlug: slug }),
                // Reconcile-only: align organizationId without disturbing the chosen brand/location.
                setOrganizationId: (id) => update({ organizationId: id }),
                // A deliberate change of the viewed org resets the scope beneath it.
                setViewedOrganizationId: (id) => update({ organizationId: id, brandId: null, locationId: null }),
                setBrandId: (id) => update({ brandId: id, locationId: null }),
                setLocationId: (id) => update({ locationId: id }),
                // Clearing storage here too would be undone a moment later: the effect above persists every
                // state change, so writing the empty state IS the clear.
                reset: () => setState(initialState),
                isSuperAdmin,
                isViewingSuperAdminOrg
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useAppContext must be used within AppProvider");
    return ctx;
};
